import {
  Box,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { GetMyDataHubPullFilesQuery } from "../../../services/api/__generated__/backend_gateway-types";
import dayjs from "../../../utils/dayjs";

type PullFilesReport = NonNullable<
  GetMyDataHubPullFilesQuery["getMyDataHubPullFiles"]
>;
type SourceStatus = PullFilesReport["sources"][number];

const SOURCE_TYPE_NAMES: Record<string, string> = {
  google_drive: "Google Drive",
  airtable: "Airtable",
  netsuite_file_cabinet: "NetSuite File",
  netsuite_suiteql: "NetSuite SuiteQL",
  trackstar_wms: "Trackstar WMS",
  gmail_email_attachment: "Gmail Attachment",
  fulfil: "Fulfil API",
};

const formatBytes = (bytes: number | null | undefined): string => {
  if (bytes == null) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDateTime = (isoString: string | null | undefined): string =>
  isoString ? dayjs.utc(isoString).local().format("MM/DD/YYYY HH:mm") : "-";

// Every time in this panel is the viewer's own zone. Naming it once in the
// header is what makes that legible — an unlabelled "12:31" is only readable
// by someone who already knows which clock it is on.
const localZoneAbbreviation = (): string => {
  try {
    return (
      new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
        .formatToParts(new Date())
        .find((part) => part.type === "timeZoneName")?.value ?? ""
    );
  } catch {
    return "";
  }
};

const statusChip = (
  source: SourceStatus,
  hasPull: boolean,
  freshnessKnown: boolean,
  cycleDelivered: boolean
) => {
  if (source.found) {
    // Without a working schedule we know the file exists but not whether it
    // is current, and green is a claim about currency — so say what we know.
    if (!freshnessKnown) {
      return <Chip size="small" variant="outlined" label="Last pulled" />;
    }
    return (
      <Chip
        size="small"
        variant="outlined"
        color="success"
        icon={<CheckCircleOutlineIcon />}
        label="Pulled"
      />
    );
  }
  if (source.cadence === "per_run") {
    return (
      <Chip size="small" variant="outlined" label="Pulled at run time" />
    );
  }
  // Before the org's first pull nothing has failed — every daily source is
  // simply unpulled, so don't paint the table red.
  if (!hasPull) {
    return <Chip size="small" variant="outlined" label="No pull yet" />;
  }
  // Nothing at all has landed this cycle: the run is still in flight, or the
  // schedule just moved and its new time hasn't come around. That is one
  // org-level fact — the "Pull overdue" chip says it once — not N failures.
  if (freshnessKnown && !cycleDelivered) {
    return <Chip size="small" variant="outlined" label="Awaiting pull" />;
  }
  // A stale file is a different problem from no file at all: the customer
  // still has data, just not today's. The Pulled At column says how old.
  if (source.file) {
    return (
      <Chip
        size="small"
        variant="outlined"
        color="error"
        icon={<ErrorOutlineIcon />}
        label="Out of date"
      />
    );
  }
  return (
    <Chip
      size="small"
      variant="outlined"
      color="error"
      icon={<ErrorOutlineIcon />}
      label="Never pulled"
    />
  );
};

export const DataPullsPanelComponent = (props: {
  loading: boolean;
  report: PullFilesReport | null;
}) => {
  const { loading, report } = props;

  const sources = report?.sources || [];
  // Failures surface above the fold; per_run (neutral) sinks below Pulled.
  const rank = (s: SourceStatus) =>
    !s.found && s.cadence !== "per_run" ? 0 : s.found ? 1 : 2;
  const sortedSources = [...sources].sort((a, b) => rank(a) - rank(b));
  const dailySources = sources.filter((s) => s.cadence !== "per_run");
  const dailyCount = dailySources.length;
  const hasPull = Boolean(report?.pullTimestamp);
  // Freshness is measured against the pull schedule; with no enabled schedule
  // there is no cycle to be late for, so no row claims to be current.
  const freshnessKnown = report?.pullIsEnabled === true;
  // Did anything at all arrive this cycle? If not, the gap is org-level.
  const cycleDelivered = dailySources.some((s) => s.found);
  const zone = localZoneAbbreviation();
  const missing =
    freshnessKnown && cycleDelivered
      ? dailySources.filter((s) => !s.found)
      : [];

  return (
    <Box
      sx={{
        height: "40%",
        minHeight: 180,
        display: "flex",
        flexDirection: "column",
        borderTop: "2px solid #e6eaf1",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1,
          backgroundColor: "#fbfcfe",
          borderBottom: "1px solid #eef1f6",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.08em",
            color: "text.secondary",
          }}
        >
          DATA PULLS
        </Typography>
        {!loading && report && (
          <>
            {report.pullOverdue ? (
              <Chip
                size="small"
                variant="outlined"
                color="error"
                icon={<ErrorOutlineIcon />}
                label="Pull overdue"
              />
            ) : !hasPull ? (
              <Chip size="small" variant="outlined" label="No pulls yet" />
            ) : missing.length > 0 ? (
              <Chip
                size="small"
                variant="outlined"
                color="error"
                icon={<ErrorOutlineIcon />}
                label={`${missing.length} file${
                  missing.length === 1 ? "" : "s"
                } not refreshed`}
              />
            ) : dailyCount === 0 ? (
              <Chip
                size="small"
                variant="outlined"
                label="All sources pull at run time"
              />
            ) : !freshnessKnown ? (
              <Chip
                size="small"
                variant="outlined"
                label="Freshness not checked"
              />
            ) : !cycleDelivered ? (
              <Chip size="small" variant="outlined" label="Awaiting pull" />
            ) : (
              <Chip
                size="small"
                variant="outlined"
                color="success"
                icon={<CheckCircleOutlineIcon />}
                label={`All ${dailyCount} daily source${
                  dailyCount === 1 ? "" : "s"
                } pulled`}
              />
            )}
            {report.pullIsEnabled === false && (
              <Chip size="small" variant="outlined" label="Pulls paused" />
            )}
            <Typography
              sx={{ fontSize: 12, color: "text.secondary", ml: "auto" }}
            >
              {/* Header on the cycle start, not the newest pull. Showing the
                  newest pull of any kind made every row with an earlier
                  timestamp look like it had failed in that pull — when a
                  single-source pull simply never touched those sources. */}
              {report.cycleStartTime
                ? `Scheduled pull: ${formatDateTime(
                    report.cycleStartTime
                  )} ${zone}`
                : report.pullTimestamp
                ? `Last pull: ${formatDateTime(report.pullTimestamp)} ${zone}`
                : "No pulled files found yet"}
            </Typography>
          </>
        )}
      </Box>

      {loading ? (
        <LinearProgress />
      ) : (
        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Pulled At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSources.map((source, idx) => (
                <TableRow key={`${source.sourceName}-${idx}`}>
                  <TableCell>{source.sourceName}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {SOURCE_TYPE_NAMES[source.sourceType] || source.sourceType}
                  </TableCell>
                  <TableCell>
                    {statusChip(
                      source,
                      hasPull,
                      freshnessKnown,
                      cycleDelivered
                    )}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {formatBytes(source.file?.sizeBytes)}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {formatDateTime(source.file?.lastModified)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
